import { useEffect, useRef, useState } from "react";
import { GridFilters } from "../gridUrlSync/useGridFiltersUrlSync";
import { OneViewFilter } from "./toLocalStorage";
import { createNewView } from "./actions/createNewView";
import { deleteFilter } from "./actions/deleteView";
import { updateView } from "./actions/updateView";
import { getMatchingView } from "./actions/getMatchingView";
import { getViews } from "./actions/getViews";
import { persistViewColumnState } from "./actions/persistViewColumnState";
import { stripViewFilterPrefix } from "./actions/stripViewFilterPrefix";
import { MRT_RowData } from "material-react-table";
import { GridColumnsType } from "../columnsExtension/useGridColumns";
import { getPreferenceFromCache, setPreference } from "src/services/PreferenceStore";

const viewFilterBaseKey = "viewFilters-" as const;
const DEFAULT_VIEW_FILTER_NAME = "grid_filter.no_options" as const;

export type ViewFilters = {
    resetSelectedFilter: () => void;
    deleteSelectedFilter: () => void;
    addNewFilterView: (filterName: string) => void;
    allFilters: OneViewFilter[];
    selectedFilter: OneViewFilter | undefined;
    selectView: (newSelectedView: OneViewFilter) => void;
    updateFilter: (options: { filterNameToUpdate: string; newFilterName: string }) => void;
    reorderViews: (reorderedFilters: OneViewFilter[]) => void;
};

export type OuterViewFilter = {
    allFilters: OneViewFilter[];
    selectedFilter: OneViewFilter | undefined;
};

type ViewFiltersOptions<DataT extends MRT_RowData = MRT_RowData> = {
    gridKey: string;
    gridFilters: GridFilters;
    columnsConfig?: GridColumnsType<DataT>;
};

const buildInitialViews = (usableGridKey: string): { defaultView: OneViewFilter; allViews: OneViewFilter[] } => {
    const allAvailableFilters = getViews({ gridKey: usableGridKey });

    const savedAllViewState = allAvailableFilters.find((v) => v.filterName === DEFAULT_VIEW_FILTER_NAME);
    const namedViews = allAvailableFilters.filter((v) => v.filterName !== DEFAULT_VIEW_FILTER_NAME);

    const defaultView: OneViewFilter = {
        filterName: DEFAULT_VIEW_FILTER_NAME,
        columnFilters: [],
        columnOrder: savedAllViewState?.columnOrder,
        columnVisibility: savedAllViewState?.columnVisibility,
    };

    return { defaultView, allViews: [defaultView, ...namedViews] };
};

export const useViewFilters = <DataT extends MRT_RowData>(options: ViewFiltersOptions<DataT>) => {
    const { gridKey, gridFilters, columnsConfig } = options;

    const usableGridKey = `${viewFilterBaseKey}${gridKey}`;
    const preferenceKey = stripViewFilterPrefix(usableGridKey);
    const { defaultView, allViews } = buildInitialViews(usableGridKey);

    const savedViewName = getPreferenceFromCache<string>("grid.views.selected", preferenceKey);
    const savedView = savedViewName ? allViews.find((v) => v.filterName === savedViewName) : undefined;

    const initialViewSelected =
        savedView ??
        getMatchingView({
            allViews,
            columnFilters: gridFilters.columnFilters,
        });

    const needsRestoreRef = useRef(savedView !== undefined && savedView.filterName !== DEFAULT_VIEW_FILTER_NAME);

    const [filtersView, _setFiltersView] = useState<OuterViewFilter>({
        allFilters: allViews,
        selectedFilter: initialViewSelected,
    });
    const setFiltersView = (newViews: OneViewFilter[], newSelected?: OneViewFilter) => {
        const savedAllViewState = newViews.find((v) => v.filterName === DEFAULT_VIEW_FILTER_NAME);
        const namedViews = newViews.filter((v) => v.filterName !== DEFAULT_VIEW_FILTER_NAME);

        const currentDefaultView: OneViewFilter = {
            filterName: DEFAULT_VIEW_FILTER_NAME,
            columnFilters: [],
            columnOrder: savedAllViewState?.columnOrder,
            columnVisibility: savedAllViewState?.columnVisibility,
        };
        const allFiltersWithDefault = [currentDefaultView, ...namedViews];
        const newSelectedFilter = newSelected === undefined ? currentDefaultView : newSelected;

        _setFiltersView({
            allFilters: allFiltersWithDefault,
            selectedFilter: newSelectedFilter,
        });
    };

    const applyColumnState = (view: OneViewFilter) => {
        if (columnsConfig === undefined) return;

        columnsConfig.setSkipViewReset(true);
        const isAllView = view.filterName === DEFAULT_VIEW_FILTER_NAME;
        columnsConfig.setSuppressPersist(!isAllView);

        if (view.columnOrder !== undefined) {
            columnsConfig.setColumnOrder(view.columnOrder);
        } else {
            columnsConfig.resetColumnOrder();
        }

        if (view.columnVisibility !== undefined) {
            columnsConfig.replaceColumnVisibility(view.columnVisibility);
        } else {
            columnsConfig.resetColumnVisibility();
        }
    };

    const selectView = (newSelectedView: OneViewFilter) => {
        let updatedAllFilters = filtersView.allFilters;

        if (filtersView.selectedFilter?.filterName === DEFAULT_VIEW_FILTER_NAME && columnsConfig !== undefined) {
            const updatedAllView: OneViewFilter = {
                ...filtersView.selectedFilter,
                columnOrder: columnsConfig.columnOrder,
                columnVisibility: columnsConfig.columnVisibility,
            };

            persistViewColumnState({
                gridKey: usableGridKey,
                viewName: DEFAULT_VIEW_FILTER_NAME,
                columnOrder: columnsConfig.columnOrder,
                columnVisibility: columnsConfig.columnVisibility,
            });

            updatedAllFilters = filtersView.allFilters.map((filter) =>
                filter.filterName === DEFAULT_VIEW_FILTER_NAME ? updatedAllView : filter,
            );
        }

        const freshSelectedView = updatedAllFilters.find((f) => f.filterName === newSelectedView.filterName);
        const viewToApply = freshSelectedView ?? newSelectedView;

        _setFiltersView({
            allFilters: updatedAllFilters,
            selectedFilter: viewToApply,
        });
        applyColumnState(viewToApply);
        setPreference("grid.views.selected", preferenceKey, viewToApply.filterName);
    };

    const addNewFilterView = (filterName: string) => {
        const newView: OneViewFilter = {
            filterName,
            columnFilters: gridFilters.columnFilters,
            columnOrder: columnsConfig?.columnOrder,
            columnVisibility: columnsConfig?.columnVisibility,
        };
        const newViews = createNewView({
            gridKey: usableGridKey,
            view: newView,
        });
        setFiltersView(newViews, newView);
        setPreference("grid.views.selected", preferenceKey, filterName);
    };

    const updateFilter = (options: { filterNameToUpdate: string; newFilterName: string }) => {
        const { filterNameToUpdate, newFilterName } = options;
        const newView: OneViewFilter = {
            filterName: newFilterName,
            columnFilters: gridFilters.columnFilters,
            columnOrder: columnsConfig?.columnOrder,
            columnVisibility: columnsConfig?.columnVisibility,
        };
        const updatedViews = updateView({
            gridKey: usableGridKey,
            previousFilterName: filterNameToUpdate,
            viewToUpdate: newView,
        });

        setFiltersView(updatedViews, newView);
        setPreference("grid.views.selected", preferenceKey, newFilterName);
    };

    const deleteSelectedFilter = () => {
        const lastSelected = filtersView.selectedFilter;
        if (lastSelected === undefined) {
            return;
        }

        const newFilters = deleteFilter({
            gridKey: usableGridKey,
            view: lastSelected,
        });
        setFiltersView(newFilters);
        gridFilters.setColumnFilters([]);
        applyColumnState(defaultView);
        setPreference("grid.views.selected", preferenceKey, DEFAULT_VIEW_FILTER_NAME);
    };

    const resetSelectedFilter = () => {
        const lastSavedVersion = filtersView.selectedFilter?.columnFilters ?? [];
        gridFilters.setColumnFilters(lastSavedVersion);
        if (filtersView.selectedFilter !== undefined) {
            applyColumnState(filtersView.selectedFilter);
        }
    };

    const reorderViews = (reorderedFilters: OneViewFilter[]) => {
        _setFiltersView((prev) => ({
            ...prev,
            allFilters: reorderedFilters,
        }));

        const preferenceKey = stripViewFilterPrefix(usableGridKey);
        const namedOrder = reorderedFilters
            .filter((v) => v.filterName !== DEFAULT_VIEW_FILTER_NAME)
            .map((v) => v.filterName);
        setPreference("grid.views.order", preferenceKey, namedOrder);
    };

    useEffect(() => {
        if (!needsRestoreRef.current) return;
        needsRestoreRef.current = false;

        if (initialViewSelected !== undefined) {
            gridFilters.setColumnFilters(initialViewSelected.columnFilters);
            applyColumnState(initialViewSelected);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return {
        ...filtersView,
        resetSelectedFilter,
        deleteSelectedFilter,
        updateFilter,
        addNewFilterView,
        selectView,
        reorderViews,
    };
};
